import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Progress } from "../ui/progress";

const STATUS = {
  running: "Rendering", paused: "Paused", paused_no_tokens: "Paused · wallet low",
  completed: "Completed", cancelled: "Cancelling", cancelled_done: "Cancelled",
};
const STATE_CLS = { done: "text-emerald-700", failed: "text-red-600", rendering: "text-blue-700", queued: "text-slate-500", cancelled: "text-slate-400" };

const BatchActions = ({ b, refetch }) => {
  const act = async (a) => {
    if (a === "cancel" && !window.confirm(`Cancel "${b.name}"? Videos not yet started will not be made.`)) return;
    if (a === "remove" && !window.confirm(`Remove "${b.name}" from the list? Finished videos are kept.`)) return;
    try {
      if (a === "remove") { await api.delete(`/studio/batches/${b.id}`); refetch(); return; } await api.post(`/studio/batches/${b.id}/${a}`); refetch(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  return (
    <div className="flex gap-1.5">
      {b.status === "running" && <Button data-testid={`batch-pause-${b.id}`} size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => act("pause")}>Pause</Button>}
      {(b.status === "paused" || b.status === "paused_no_tokens") && <Button data-testid={`batch-resume-${b.id}`} size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => act("resume")}>Resume</Button>}
      {["completed", "cancelled_done"].includes(b.status) && <Button data-testid={`batch-remove-${b.id}`} size="sm" variant="ghost" className="h-6 text-[11px] px-2 text-slate-500" onClick={() => act("remove")}>Remove</Button>}
      {["running", "paused", "paused_no_tokens"].includes(b.status) && <Button data-testid={`batch-cancel-${b.id}`} size="sm" variant="ghost" className="h-6 text-[11px] px-2 text-red-600" onClick={() => act("cancel")}>Cancel</Button>}
    </div>
  );
};

const BatchDetail = ({ id, onClose, onOpenContent }) => {
  const { data } = useQuery({ queryKey: ["batch", id], queryFn: () => api.get(`/studio/batches/${id}?limit=200`).then((r) => r.data), refetchInterval: 6000, enabled: !!id });
  const open = async (it) => {
    const { data: c } = await api.get(`/studio/content/${it.id}`);
    onClose();
    onOpenContent(c);
  };
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="batch-detail-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border-slate-200">
        <DialogHeader><DialogTitle>{data?.name || "Batch"}</DialogTitle></DialogHeader>
        {data?.pause_reason && <div data-testid="batch-pause-reason" className="text-xs text-amber-700 bg-amber-50 rounded p-2">{data.pause_reason}</div>}
        <div className="divide-y divide-slate-100">
          {(data?.items || []).map((it) => (
            <button key={it.id} data-testid={`batch-item-${it.batch_index}`} disabled={it.batch_state !== "done" && it.batch_state !== "failed"}
              onClick={() => open(it)} className="w-full text-left py-2 flex items-center gap-3 hover:bg-slate-50 disabled:hover:bg-transparent">
              <span className="text-sm flex-1 truncate">{it.title}</span>
              <span className={`text-[11px] font-mono uppercase ${STATE_CLS[it.batch_state] || ""}`}>
                {it.batch_state === "rendering" ? `${Math.round((it.video_render_progress || 0) * 100)}%` : it.batch_state}
              </span>
            </button>
          ))}
        </div>
        {data && data.total > 200 && <p className="text-[11px] text-slate-500">Showing the first 200 of {data.total} videos.</p>}
      </DialogContent>
    </Dialog>
  );
};

export const BatchList = ({ onOpenContent }) => {
  const [detail, setDetail] = useState(null);
  const { data: batches = [], refetch } = useQuery({ queryKey: ["batches"], queryFn: () => api.get("/studio/batches").then((r) => r.data), refetchInterval: 8000 });
  if (!batches.length) return null;
  return (
    <div data-testid="batch-list" className="space-y-2 mb-4">
      {batches.slice(0, 5).map((b) => {
        const c = b.counts || {};
        const finished = (c.done || 0) + (c.failed || 0);
        return (
          <div key={b.id} data-testid={`batch-card-${b.id}`} className="bg-white border border-slate-200 rounded-md p-3">
            <div className="flex items-center justify-between gap-2">
              <button data-testid={`batch-view-${b.id}`} onClick={() => setDetail(b.id)} className="text-sm font-semibold truncate text-left hover:text-blue-700">{b.name}</button>
              <span data-testid={`batch-status-${b.id}`} className="text-[10px] font-mono uppercase text-slate-500 shrink-0">{STATUS[b.status] || b.status}</span>
            </div>
            <Progress value={(finished / Math.max(1, b.total)) * 100} className={`h-1.5 mt-2 ${b.status.startsWith("cancelled") ? "opacity-40" : ""}`} />
            <div className="flex items-center justify-between mt-1.5">
              <span data-testid={`batch-counts-${b.id}`} className="text-[11px] text-slate-600">
                {c.done || 0}/{b.total} done · {c.rendering || 0} rendering · {c.failed || 0} failed
              </span>
              <BatchActions b={b} refetch={refetch} />
            </div>
          </div>
        );
      })}
      <BatchDetail id={detail} onClose={() => setDetail(null)} onOpenContent={onOpenContent} />
    </div>
  );
};
